import { store } from "../utils/store";
import { getOfficeDetails } from "../screens/AllLeads/components/stepper/StepperService";

/** Admin and org owners can assign any department, team, or member. */
export function isOrgWideAssigner(auth?: {
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  hierarchyLevel?: string | number;
} | null): boolean {
  if (!auth) return false;
  if (auth.isAdmin || auth.isSuperAdmin) return true;
  return String(auth.hierarchyLevel ?? "") === "1";
}

export function parseTeamIdList(raw: unknown): string[] {
  if (raw == null || raw === "" || raw === "NA") return [];
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id).replace(/['"]/g, "").trim()).filter((id) => id && id !== "NA");
  }
  const text = String(raw).trim();
  if (!text || text === "NA") return [];
  try {
    if (text.startsWith("[")) {
      const parsed = JSON.parse(text);
      return parseTeamIdList(parsed);
    }
  } catch {
    /* comma-separated */
  }
  return text
    .split(",")
    .map((s) => s.trim().replace(/['"]/g, ""))
    .filter((id) => id && id !== "NA");
}

/**
 * null = this user may assign across the organization.
 * string[] = only these team ids (and their members).
 */
export async function loadViewerTeamIds(): Promise<string[] | null> {
  const auth = store.getState().auth;
  if (isOrgWideAssigner(auth)) return null;
  const userId = String(auth?.userId || "").trim();
  if (!userId) return [];
  try {
    const office = await getOfficeDetails(userId);
    const ids = parseTeamIdList(office?.data?.team_id);
    return ids;
  } catch {
    return [];
  }
}

export type ViewerAssignScope = {
  /** org = admin can pick any team. leader = own team only, all its members. member = self only. */
  role: "org" | "leader" | "member";
  teamIds: string[];
  teams: { id: string; label: string }[];
  userId: string;
  userName: string;
};

function isTeamLeaderRole(roleName: unknown): boolean {
  const role = String(roleName || "").toLowerCase().replace(/[_-]+/g, " ");
  if (!role.trim()) return false;
  if (
    /\btl\b/.test(role) ||
    role.includes("team lead") ||
    role.includes("teamlead") ||
    role.includes("team leader") ||
    role.includes("leader")
  ) {
    return true;
  }
  if (role.includes("head") || role.includes("manager")) return true;
  return false;
}

let assignScopePromise: Promise<ViewerAssignScope> | null = null;

/** Who this login may assign, based on office team role. */
export function loadViewerAssignScope(): Promise<ViewerAssignScope> {
  if (!assignScopePromise) {
    assignScopePromise = (async () => {
      const auth = store.getState().auth || {};
      const userId = String(auth.userId || "").trim();
      const userName = String(auth.userName || "You");
      if (isOrgWideAssigner(auth)) {
        return { role: "org", teamIds: [], teams: [], userId, userName };
      }
      if (!userId) {
        return { role: "member", teamIds: [], teams: [], userId, userName };
      }
      try {
        const office = await getOfficeDetails(userId);
        const data = office?.data || {};
        const teamIds = parseTeamIdList(data.team_id);
        const nameList = String(data.team_name || "")
          .split(",")
          .map((s) => s.trim().replace(/^['"]|['"]$/g, ""))
          .filter((s) => s && s !== "NA");
        const teams = teamIds.map((id, index) => ({
          id,
          label: nameList[index] || nameList[0] || "Your team",
        }));
        const authAny = auth as unknown as {
          hierarchyName?: string;
          teamRoleName?: string;
        };
        const role = isTeamLeaderRole(
          `${data.teamRoleName || ""} ${data.hierarchyName || ""} ${data.roleName || ""} ${data.roleSlug || ""} ${authAny.hierarchyName || ""} ${authAny.teamRoleName || ""}`
        )
          ? "leader"
          : "member";
        return { role, teamIds, teams, userId, userName };
      } catch {
        return { role: "member", teamIds: [], teams: [], userId, userName };
      }
    })();
  }
  return assignScopePromise;
}

export function filterByViewerTeams<T extends Record<string, any>>(
  rows: T[],
  viewerTeamIds: string[] | null,
  idOf: (row: T) => string = (row) =>
    String((row as any).id ?? (row as any).value ?? "")
): T[] {
  if (viewerTeamIds == null) return rows;
  const allow = new Set(viewerTeamIds.map(String));
  return rows.filter((row) => allow.has(idOf(row)));
}
